import { env } from '@/lib/env';
import { CSRF_FAILURE_HEADER, csrfHeaders, isCsrfFailure, refreshCsrfToken } from './csrf';

/**
 * Keeping a sign-in alive (docs/plans/jwt-cookie-auth.md, D3/D5).
 *
 * Identity is the Cognito ID token in an HttpOnly cookie, valid for 15 minutes. When it expires the
 * server answers 401; `POST /auth/refresh` re-issues it from the refresh cookie — but only within a
 * grace window after expiry, so a user who walked away is signed out while one who was merely
 * between requests continues without noticing. The refresh is therefore ALWAYS a reaction to a 401
 * on a real request (the API wrapper, an SSE re-open, the "Stay signed in" button), never a timer:
 * a timer would keep an idle tab signed in forever and defeat the idle timeout.
 *
 * Single-flight: a page that fires ten requests as the token lapses gets ten 401s and ONE refresh.
 *
 * This module sits below both `api.ts` and `authService.ts` (it imports neither) so either can use
 * it without an import cycle.
 */

/** How long before the sign-out deadline `AuthProvider` shows the inactivity warning. */
export const SESSION_WARNING_LEAD_MS = 2 * 60_000;
/** How soon `AuthProvider` looks again when a probe could not settle the question (server unreachable, clock skew). */
export const SESSION_PROBE_RETRY_MS = 30_000;

export type SessionExpiryListener = (sessionExpiresAt: string) => void;

/**
 * `renewed`: cookies re-issued, carry on. `refused`: the sign-in is over (idle grace passed, refresh
 * token revoked or expired, account deactivated) — sign the user out. `unavailable`: the server could
 * not reach Cognito (503) or could not be reached at all — the sign-in may be perfectly good, so do
 * NOT sign out; fail the current action and let the next 401 try again.
 */
export type RefreshOutcome = 'renewed' | 'refused' | 'unavailable';

const listeners = new Set<SessionExpiryListener>();

/** Be told whenever a refresh establishes a new deadline (ISO instant), so timers can reschedule. */
export function onSessionExpiryChange(listener: SessionExpiryListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publishSessionExpiry(sessionExpiresAt: string): void {
  for (const listener of listeners) listener(sessionExpiresAt);
}

let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * Ask the server for a fresh ID token. See {@link RefreshOutcome} for what the caller does with each
 * answer; on `renewed` the new deadline has already been published. Concurrent callers share one
 * request.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  if (!inFlight) {
    inFlight = attemptRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function attemptRefresh(): Promise<RefreshOutcome> {
  try {
    const send = () =>
      fetch(`${env.API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders('POST'),
      });
    let response = await send();
    // Refresh is a POST like any other and needs the CSRF token; restock it once if it was missing.
    if (isCsrfFailure(response.status, response.headers.get(CSRF_FAILURE_HEADER)) && (await refreshCsrfToken())) {
      response = await send();
    }
    if (response.status === 401 || response.status === 403) return 'refused';
    if (!response.ok) return 'unavailable';
    const body = (await response.json()) as { sessionExpiresAt?: unknown };
    if (typeof body.sessionExpiresAt === 'string') publishSessionExpiry(body.sessionExpiresAt);
    return 'renewed';
  } catch {
    return 'unavailable';
  }
}

/** Test seam: forget an in-flight refresh so files are order-independent. */
export function resetSessionRefreshForTests(): void {
  inFlight = null;
  listeners.clear();
}
